/**
 * SuperAdmin parent layout route — matches /superadmin (prefix).
 *
 * REQ-AUTH-015 — fresh-step-up-on-every-visit guard.
 *
 * SuperAdmin is the highest-privilege surface (platform-wide tenant + tier
 * management, recovery flows, audit-chain integrity). Every visit to
 * /superadmin/* requires a verified second factor THIS session plus a FRESH
 * backend step-up stamp — not just a session, not an MFA challenge from
 * yesterday, but proof the operator re-authenticated within minutes.
 *
 * ⚠️ Policy relaxation (Paul, 2026-06-11 — SA-1): the guard originally
 * demanded a WebAuthn (FIDO2) factor, but (a) it read `mfa_factor_type` /
 * `mfa_challenge_at` as top-level JWT claims that real Supabase tokens never
 * carry (only the test mocks fabricated them), and (b) WebAuthn MFA cannot
 * currently be enabled on the Supabase project ("not currently supported"
 * for the plan) — so no operator could ever pass. Relaxed to accept ANY
 * verified second factor (TOTP today). RE-TIGHTEN to WebAuthn-only when the
 * Supabase plan supports enrolling it: restore `factor: 'webauthn'` on the
 * step-up redirect and check the freshest `amr[]` entry's method.
 *
 * Guard logic (in order):
 *  0. VITE_SUPERADMIN_ENABLED ≠ 'true' → generic disabled notice
 *                          (SuperAdminFlagDisabled), before session/role are
 *                          ever read — same for every visitor (2026-08-22)
 *  1. No session       → /auth/login
 *  2. Role ≠ super_admin → neutral "not available" notice
 *                          (SuperAdminNotAvailable), NOT /auth/login — a
 *                          signed-in wrong-role user bounced to the login
 *                          form read as an unexplained logout (P3 #28,
 *                          below). The signed-out anti-enumeration case is
 *                          fully handled by step 1, before role is read.
 *  3. aal ≠ 'aal2'     → /auth/refresh-step-up?return=/superadmin
 *  4. Backend step-up stamp (`app_metadata.mfa_challenge_at`) missing or
 *     older than 5 min → /auth/refresh-step-up?return=/superadmin
 *  5. Otherwise         → <Outlet /> (children render normally)
 *
 * Claims are read from the access_token JWT — REAL Supabase claims only:
 * `aal` (set to 'aal2' after an MFA verify) and `app_metadata` (`role`, plus
 * `mfa_challenge_at` stamped by POST /v1/auth/refresh-step-up, which
 * re-verifies aal2 + amr freshness server-side before stamping). We decode
 * the payload locally with `atob` — no signature verification client-side is
 * fine because the JWT is already trusted by the auth listener that put it
 * in the session, and the backend re-verifies the signature on every API call.
 *
 * Defense-in-depth v1: the entire /superadmin/* surface is feature-flagged
 * off by default via VITE_SUPERADMIN_ENABLED. When the flag isn't set to
 * 'true', the very first branch renders a generic disabled notice
 * (SuperAdminFlagDisabled) — regardless of session or role. Until
 * 2026-08-22 this was a silent `<Navigate to="/app" />`, which
 * /app/_layout.tsx then re-routed to /admin/client-book for any
 * FIRM_STAFF-shaped role including super_admin — a real platform operator
 * on a flag-off build was dumped on the firm console with zero explanation.
 *
 * Batch 17 (2026-05-20) claimed all 12 child routes were wired to the live
 * /v1/superadmin/* API. That was never true and went unchecked for three
 * months (corrected 2026-08-22, e2e-20260821 finding). The fake-data files
 * (`lib/fake-data/superadmin.ts` and `lib/fake-data/audit-forensic.ts`) ARE
 * deleted, so nothing renders fabricated numbers — but only 5 of the 12
 * routes call a real backend endpoint at all, and only 3 of those 5 are
 * actually under /v1/superadmin/*:
 *   LIVE   index.tsx         — useSuperadminTenants (GET /v1/superadmin/tenants)
 *   LIVE   tenants/index.tsx — useSuperadminTenants + useCreateTenant
 *   LIVE   tenants/$id.tsx   — the full tenant-detail surface: suspend /
 *                              reactivate / delete-request / billing-override /
 *                              billing-refund / force-password-reset /
 *                              impersonate / update, all under /v1/superadmin/*
 *   LIVE   audit.tsx         — useAuditLog + fetchAuditExport (the general
 *                              /v1/audit/* surface, NOT /v1/superadmin/*)
 *   LIVE   recovery.tsx      — useAdminRecoveryRequests + approve/reject/
 *                              reissue (/v1/admin/recovery-requests, NOT
 *                              /v1/superadmin/*)
 *   STATIC health.tsx        — no API call BY DESIGN: links out to Azure
 *                              Application Insights instead of faking an
 *                              in-app dashboard (see its own file header)
 *   STATIC features.tsx, pricing.tsx, sentinel-rules.tsx
 *                            — InternalComingSoon placeholder, no data
 *   STATIC settings.tsx      — hardcoded "not yet configured" copy
 *   STATIC subprocessors.tsx — hardcoded register (mirrors the public page)
 *   STATIC tiers.tsx         — static FEATURE_MATRIX reference table
 * The build-time flag remains as a safety net so a super_admin can't
 * accidentally reach the destructive operations surface (suspend / delete /
 * impersonate / billing override — all on tenants/$id.tsx) from a build
 * that hasn't been promoted yet.
 *
 * Phase 9.6 may add: "factor was bound to this device" check, hardware-key
 * attestation enforcement, sentinel rate-limit on stale assertions.
 */

import { useEffect } from 'react';
import { createFileRoute, Link, Navigate, Outlet } from '@tanstack/react-router';
import { useSession } from '../../lib/use-session';
import { clearActAsContext } from '../../lib/act-as-context';
import { forgetLastKnownActAsSubject } from '../../lib/acting/context';
import { canAccess } from '../../lib/authz/route-policy';
import { landingRouteForRole } from '../../lib/auth-landing';
import type { UserRole } from '../../lib/api';

/**
 * Build-time feature flag controlling whether /superadmin/* is reachable
 * at all. Defaults closed: any value other than the literal string 'true'
 * sends the user to /app on the first render.
 *
 * Rationale (post-Batch-17): the 12 child routes now consume the live
 * /v1/superadmin/* API, but the destructive operations (suspend / delete /
 * impersonate / billing override) are high-blast-radius. Keep the flag
 * closed in builds where an Elreign staff super_admin hasn't yet been
 * onboarded to the operator runbook. The flag is intentionally NOT
 * documented for customer-facing operators — set only in
 * developer-local `.env.local` or a dedicated operator build.
 *
 * We read the env at render time (not module-load time) so vitest can
 * flip the flag per test via vi.stubEnv. Vite still tree-shakes references
 * to `import.meta.env.*` at build time, so the read is a no-cost lookup
 * in prod and adapts to runtime injection in dev.
 */
function isSuperadminEnabled(): boolean {
  return import.meta.env['VITE_SUPERADMIN_ENABLED'] === 'true';
}

/** Freshness window for the backend step-up stamp — 5 minutes per REQ-AUTH-015. */
const FRESH_WINDOW_SECONDS = 300;

/**
 * Shape of the access_token JWT payload bits we read in this guard.
 *
 * REAL Supabase claims only. The previous guard read `mfa_factor_type` /
 * `mfa_challenge_at` as TOP-LEVEL claims — Supabase issues neither (it issues
 * `aal` + `amr[]`; our backend refresh-step-up stamps
 * `app_metadata.mfa_challenge_at`). Those fake claims existed only in test
 * mocks, so the gate was unpassable by any real operator (SA-1, 2026-06-11).
 */
interface AccessTokenClaims {
  aal?: string;
  app_metadata?: { role?: string; mfa_challenge_at?: number };
}

/**
 * Decode the payload of a JWT (`header.payload.sig`) without verifying the
 * signature. Returns `{}` for any malformed input so the guard fails closed
 * (no claims → no role → redirect).
 */
function decodeJwtPayload(jwt: string): AccessTokenClaims {
  try {
    const parts = jwt.split('.');
    if (parts.length < 2 || !parts[1]) return {};
    // Convert base64url → base64 for atob.
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4 === 0 ? b64 : b64 + '='.repeat(4 - (b64.length % 4));
    const json = atob(pad);
    return JSON.parse(json) as AccessTokenClaims;
  } catch {
    return {};
  }
}

export const Route = createFileRoute('/superadmin')({
  // S-1.3: entering the platform console drops any act-as / impersonation context.
  // A super_admin makes cross-tenant changes here and must not carry an active
  // impersonation/act-as token into it; runs before the console renders.
  beforeLoad: () => {
    clearActAsContext();
    // AND FORGET WHO IT WAS. Entering the platform console is a DELIBERATE
    // exit, exactly as entering the firm console is, and `routes/admin/_layout`,
    // `lib/sign-out` and `lib/onboarding-reset` all say so out loud here.
    // This one did not (review 2 of the rulings wave, finding R4).
    //
    // `lib/acting/last-act-as.ts` explains why the call has to be explicit:
    // from a store subscription, a deliberate exit and an expiry are the SAME
    // event (`clear()`), so forgetting automatically would delete the very fact
    // renewal came for. The exit is the only place it belongs.
    //
    // What it cost: /app's guard reads that sticky subject to tell "your 15
    // minutes ran out" (show the re-enter card) from "you left on purpose"
    // (bounce to the console). A super_admin who left act-as through THIS
    // console and then opened an /app URL was offered a client they had
    // already stepped out of, and told their time was up when it was not.
    forgetLastKnownActAsSubject();
  },
  component: SuperAdminLayout,
});

/**
 * Persistent visual indicator for the super-admin surface (H-54).
 *
 * A 65-year-old fiduciary attorney in super-admin mode is making
 * cross-tenant changes that could touch every customer firm. Nothing on
 * the page tells them they're in that high-stakes mode — the only signal
 * is the URL prefix. This strip is the explicit one: red/brass gradient,
 * sticky to the top of the viewport, an obvious "leave super-admin" exit.
 *
 * Side-effect: stamps `<html data-mode="superadmin">` so global CSS can
 * target the mode for additional decoration (e.g. desaturating brand
 * accents or sharpening borders). The flag is removed on unmount.
 */
function SuperAdminModeStrip() {
  useEffect(() => {
    const html = document.documentElement;
    const prev = html.getAttribute('data-mode');
    html.setAttribute('data-mode', 'superadmin');
    return () => {
      if (prev) html.setAttribute('data-mode', prev);
      else html.removeAttribute('data-mode');
    };
  }, []);

  return (
    <div
      role="banner"
      aria-label="Super-admin mode"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 9500,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: '8px 16px',
        background:
          'linear-gradient(90deg, #7a1d1d 0%, #b3641d 100%)',
        color: '#fff',
        fontFamily: 'var(--sans)',
        fontSize: 12.5,
        fontWeight: 600,
        letterSpacing: '0.04em',
        borderBottom: '1px solid rgba(0,0,0,0.25)',
        boxShadow: '0 1px 0 rgba(255,255,255,0.08) inset',
      }}
    >
      <span style={{ textTransform: 'uppercase' }}>
        Super-admin mode: actions affect every tenant
      </span>
      <Link
        to="/app/dashboard"
        style={{
          color: '#fff',
          textDecoration: 'none',
          padding: '4px 10px',
          borderRadius: 6,
          border: '1px solid rgba(255,255,255,0.5)',
          fontSize: 12,
          fontWeight: 600,
        }}
      >
        Leave super-admin &rarr;
      </Link>
    </div>
  );
}

function SuperAdminLayout() {
  const { session, loading } = useSession();

  // -1. Feature flag — surface is off in every build until VITE_SUPERADMIN_ENABLED
  //     is explicitly set to the string 'true'. This is checked BEFORE the
  //     hydration/session/role gates so a misconfigured prod build can never
  //     leak the route even to a legitimately super_admin operator.
  //
  //     2026-08-22 fix (e2e-20260821 finding): this used to be a silent
  //     `<Navigate to="/app" />`. /app/_layout.tsx then re-routes any
  //     FIRM_STAFF-shaped role — super_admin included — to /admin/client-book,
  //     so a REAL super_admin signing in on a flag-off build was dumped on the
  //     firm Client Book with zero explanation (login → /superadmin → /app →
  //     /admin/client-book, silently). Render an honest notice instead. This
  //     keeps the exact same fail-closed property the comment above describes
  //     — still checked before session/role are read, still renders nothing
  //     from the session/role/step-up state, so it discloses no more to an
  //     anonymous prober than "this build has the surface off" (the route's
  //     existence is already visible in the client bundle regardless).
  if (!isSuperadminEnabled()) {
    return <SuperAdminFlagDisabled />;
  }

  // 0. AuthProvider is still hydrating — render nothing rather than flash a
  //    redirect to /auth/login (which would itself bounce back here if a
  //    session does end up loading).
  if (loading) {
    return null;
  }

  // 1. No session → sign-in. Guard never assumes session is non-null below.
  if (!session) {
    return <Navigate to="/auth/login" />;
  }

  // 2. Decode access_token claims. If decode fails, claims is {} and the
  //    role check below catches it.
  const claims = decodeJwtPayload(session.access_token);

  // S-2.1: allow-set from the central route-policy. normalizeRole resolves the
  // super_admin/superadmin spelling drift, so a legacy-spelled super admin is no
  // longer locked out of the platform console (it previously was — /admin accepted
  // both spellings but this guard accepted only the canonical one).
  const role = claims.app_metadata?.role ?? '';
  if (!canAccess('superadmin', role)) {
    // P3 #28: this branch only runs for a SIGNED-IN session (step 1 above
    // already redirected an unauthenticated visitor to /auth/login, so the
    // anti-enumeration concern for a signed-OUT prober never reaches here).
    // Bouncing an authenticated-but-wrong-role user back to the sign-in FORM
    // read as an unexplained logout — show a neutral "not available" notice
    // instead, routed home for whatever role they actually have.
    return <SuperAdminNotAvailable role={role as UserRole} />;
  }

  // Step-up redirect target. Built once and reused for both the aal and
  // stale-stamp branches. NO `factor=webauthn` — the step-up screen uses
  // whatever second factor the operator actually has (TOTP today; WebAuthn
  // again once the Supabase plan can enrol it). Demanding webauthn here while
  // the project cannot enrol a webauthn factor made the surface unreachable
  // for every operator (SA-1). Relaxed to "any verified second factor +
  // fresh backend step-up stamp" per Paul, 2026-06-11. Re-tighten when
  // WebAuthn MFA is available: add `factor: 'webauthn'` back AND check the
  // freshest amr entry's method.
  const stepUp = (
    <Navigate
      to="/auth/refresh-step-up"
      search={{ return: '/superadmin' }}
    />
  );

  // 3. A real second factor must have been verified THIS session — Supabase
  //    only issues `aal: 'aal2'` after an MFA verify. (TOTP qualifies.)
  if (claims.aal !== 'aal2') {
    return stepUp;
  }

  // 4. Freshness — the BACKEND-stamped step-up timestamp
  //    (`app_metadata.mfa_challenge_at`, written by POST /v1/auth/refresh-step-up
  //    after it re-verifies aal2 + amr freshness server-side) must be ≤ 5
  //    minutes old. Missing stamp (challengeAt = 0) ⇒ fails the window.
  const challengeAt = claims.app_metadata?.mfa_challenge_at ?? 0;
  const ageSeconds = Math.floor(Date.now() / 1000) - challengeAt;
  if (ageSeconds > FRESH_WINDOW_SECONDS) {
    return stepUp;
  }

  // 5. All gates passed — render children, with the persistent mode
  //    indicator strip stuck to the top of the viewport (H-54).
  return (
    <>
      <SuperAdminModeStrip />
      <Outlet />
    </>
  );
}

/**
 * Flag-off notice (2026-08-22 fix, e2e-20260821 finding) — shown for EVERY
 * visitor to /superadmin/* when VITE_SUPERADMIN_ENABLED isn't 'true',
 * regardless of session or role (see the `-1.` guard comment above: this
 * still runs before session/role are ever read, so it discloses nothing
 * about who's asking). Replaces the old invisible `<Navigate to="/app" />`,
 * which /app/_layout.tsx silently forwarded to /admin/client-book for a real
 * super_admin — landing a platform operator on the firm console with no clue
 * why. Deliberately generic copy: no mention of how to enable the flag, no
 * env var name, no operator contact — just enough to stop the visitor
 * thinking they lost access or hit a bug.
 */
function SuperAdminFlagDisabled() {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: 'var(--bg)',
      }}
    >
      <div className="card" style={{ padding: '32px 36px', maxWidth: 440, textAlign: 'center' }}>
        <h1
          style={{
            fontSize: 19,
            fontFamily: 'var(--serif)',
            fontWeight: 600,
            color: 'var(--ink)',
            margin: '0 0 10px',
          }}
        >
          Superadmin console is disabled in this build.
        </h1>
        <p style={{ fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.6, margin: '0 0 20px' }}>
          This deployment doesn&rsquo;t have the platform-operator surface turned on.
        </p>
        <Link
          to="/app/dashboard"
          style={{ fontSize: 14, fontWeight: 600, color: 'var(--primary-600)', textDecoration: 'none' }}
        >
          Back to your dashboard
        </Link>
      </div>
    </div>
  );
}

/**
 * P3 #28 (2026-07-11 firm rehearsal) — a SIGNED-IN user with insufficient
 * role hitting the old `<Navigate to="/auth/login" />` bounce (below) landed
 * back on the sign-in FORM while already authenticated, which read as an
 * unexplained logout. The anti-enumeration rationale in the guard's header
 * comment is about a SIGNED-OUT prober — that case is handled earlier (step
 * 1, before `role` is ever read) and is untouched by this component. An
 * authenticated user who simply lacks the super_admin role isn't a probing
 * threat; showing them a minimal neutral notice (no sidebar, no super-admin
 * chrome) instead of the login form is both more honest and no less safe.
 */
function SuperAdminNotAvailable({ role }: { role: UserRole | undefined }) {
  const home = landingRouteForRole(role);
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: 'var(--bg)',
      }}
    >
      <div className="card" style={{ padding: '32px 36px', maxWidth: 440, textAlign: 'center' }}>
        <h1
          style={{
            fontSize: 19,
            fontFamily: 'var(--serif)',
            fontWeight: 600,
            color: 'var(--ink)',
            margin: '0 0 10px',
          }}
        >
          Not available.
        </h1>
        <p style={{ fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.6, margin: '0 0 20px' }}>
          This surface isn&rsquo;t available for your account.
        </p>
        <Link
          to={home}
          style={{ fontSize: 14, fontWeight: 600, color: 'var(--primary-600)', textDecoration: 'none' }}
        >
          Back to your dashboard
        </Link>
      </div>
    </div>
  );
}

export default SuperAdminLayout;
